import {
  Button,
  Card,
  Center,
  Container,
  Group,
  Input,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useApiUrl, useCustom } from "@refinedev/core";
import { useForm } from "@refinedev/mantine";
import { ZendeskTicket } from "entities/zendesk";
import { Link } from "react-router-dom";

export const ZendeskList = () => {
  const apiUrl = useApiUrl();

  const { getInputProps, onSubmit } = useForm({
    initialValues: {
      email: "",
    },
  });

  const {
    data: zendeskRequest,
    isLoading,
    isFetching,
    isFetched,
    refetch,
  } = useCustom<ZendeskTicket[]>({
    url: `${apiUrl}/zendesk/tickets`,
    method: "get",
    config: {
      query: {
        requester: getInputProps("email").value,
      },
    },
    queryOptions: {
      enabled: false,
    },
  });

  const assigneeIds = Array.from(
    new Set(zendeskRequest?.data.map((t) => t.assignee_id))
  );

  const { data: assignees } = useCustom<{ id: number; name: string }[]>({
    url: `${apiUrl}/zendesk/authors`,
    method: "get",
    config: {
      query: {
        author_ids: assigneeIds.join(","),
      },
    },
    queryOptions: {
      enabled: assigneeIds.length > 0,
    },
  });

  const handleSubmit = (values: { email: string }) => {
    if (!values.email) return;
    refetch();
  };

  const ths = (
    <tr>
      <th>ID</th>
      <th>Subject</th>
      <th>Created At</th>
      <th>Assignee</th>
    </tr>
  );

  const rows = zendeskRequest?.data.map((element) => (
    <tr key={element.id}>
      <td>
        <Link
          to={`/zendesk/tickets/${element.id}/comments`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {element.id}
        </Link>
      </td>
      <td>{element.subject}</td>
      <td>{element.created_at}</td>
      <td>
        {
          assignees?.data.find(
            (assignee) => assignee.id === element.assignee_id
          )?.name ?? element.assignee_id
        }
      </td>
    </tr>
  ));

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="lg">
        Zendesk
      </Title>

      <Card withBorder shadow="sm" p="lg" radius="md">
        <Stack>
          <Group position="apart">
            <div>
              <Text fw={500} size="lg">
                Zendesk Ticket
              </Text>
              <Text c="dimmed" size="sm">
                Find the user zendesk tickets by email
              </Text>
            </div>
          </Group>

          <form onSubmit={onSubmit(handleSubmit)}>
            <Group>
              <Input
                {...getInputProps("email")}
                placeholder="user email"
                miw={300}
              />
              <Button title="search" type="button" onClick={() => refetch()}>
                Search
              </Button>
            </Group>
          </form>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" p="lg" radius="md" mt="lg">
        {isLoading && isFetching && (
          <Center py="xl">
            <Loader />
          </Center>
        )}

        {!zendeskRequest && !isFetching && (
          <Center py="xl">
            <Stack align="center">
              <Text size="xl" fw={600}>
                Search Zendesk Tickets
              </Text>
              <Text c="dimmed">Enter an email and click search to begin.</Text>
            </Stack>
          </Center>
        )}

        {zendeskRequest && isFetched && (
          <Table highlightOnHover pos="relative">
            <thead>{ths}</thead>
            <tbody>{rows}</tbody>
          </Table>
        )}

        {!isLoading && zendeskRequest?.data.length === 0 && (
          <Center py="xl">
            <Text size="lg" c="dimmed">
              No tickets found for the provided email.
            </Text>
          </Center>
        )}
      </Card>
    </Container>
  );
};
